import React, { useState } from "react";
import { buzzLight } from "./components";
import { BRANDS } from "./brands";

// v3.11: badge grid only — the GD logo moved OUT of the lockup to the app
// header (top-left home button), so the 10 store badges take the FULL row
// width and the brand logos render big enough to read. Tap a store to
// filter the Deals tab, tap again to clear. Active store gets a subtle
// halo in its own color (v3.9); siblings are never dimmed.
const FOODS = ["ramen", "boba", "croissant", "sushi", "sourdough", "coldbrew"];

const LOGO_EXT = { coop: "svg", saveonfoods: "jpg" };
const logoSrc = (id) => `${process.env.PUBLIC_URL}/brands/${id}.${LOGO_EXT[id] || "png"}`;

// vibrant flyer palette — matches the deal cards in weekly.js
export const FLYER_COLORS = [
  "#E63B2E", "#FF7A1A", "#FFC61A", "#37B24D", "#12B5A5",
  "#2E9BE6", "#F0567C", "#8B5CC4", "#E63B2E", "#FF7A1A",
];
export const flyerColor = (i) => FLYER_COLORS[i % FLYER_COLORS.length];

export default function BubbleLogo({ store = "all", onStoreChange = () => {}, counts = {} }) {
  const [failed, setFailed] = useState({});
  const ordered = [...BRANDS].sort((a, b) => (counts[b.id] || 0) - (counts[a.id] || 0));

  const activeBrand = BRANDS.find((b) => b.id === store);
  const caption = activeBrand
    ? `${activeBrand.label} · ${counts[store] || 0} deals — tap again to clear`
    : "tap a store to see its deals";

  return (
    <div style={{ textAlign: "center" }}>
      <style>{`@keyframes foodPop{0%{transform:scale(0);opacity:0}55%{transform:scale(1.15);opacity:1}75%{transform:scale(.94)}100%{transform:scale(1);opacity:1}}`}</style>

      {/* food spheres: lined up on top, popping in one by one */}
      <div style={{ display: "flex", justifyContent: "center", gap: 12, marginBottom: 16 }}>
        {FOODS.map((f, i) => (
          <div
            key={f}
            style={{
              width: 56, height: 56, borderRadius: "50%", overflow: "hidden",
              position: "relative", flexShrink: 0,
              boxShadow: "0 3px 8px rgba(0,0,0,.14)",
              animation: `foodPop .55s cubic-bezier(.22,1,.36,1) ${0.15 + i * 0.13}s both`,
            }}
          >
            <img
              src={`${process.env.PUBLIC_URL}/foods/${f}.webp`}
              alt={f}
              draggable={false}
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", pointerEvents: "none" }}
            />
          </div>
        ))}
      </div>

      {/* v3.11: full-width badge grid (no side logo squeezing it) */}
      <div style={{ maxWidth: 640, margin: "0 auto", padding: "0 4px" }}>
        <div
          style={{
            display: "grid", gridTemplateColumns: "repeat(5, 1fr)",
            gap: 12, width: "100%",
          }}
        >
          {ordered.map((b, i) => {
            const active = store === b.id;
            const showMono = failed[b.id];
            const ring = flyerColor(i);
            return (
              // padding-top square lock: the badge box is exactly as tall as
              // it is wide, so grid stretch can never oval it (v3.9)
              <div
                key={b.id}
                style={{ position: "relative", width: "100%", paddingTop: "100%", alignSelf: "start" }}
              >
                <button
                  aria-label={`Show ${b.label} deals`}
                  aria-pressed={active}
                  onClick={() => {
                    buzzLight();
                    onStoreChange(store === b.id ? "all" : b.id);
                  }}
                  style={{
                    position: "absolute", inset: 0, width: "100%", height: "100%",
                    borderRadius: "50%", border: "none",
                    padding: "4.5%", background: ring, cursor: "pointer",
                    transform: active ? "scale(1.06)" : "scale(1)",
                    transition: "transform .28s cubic-bezier(.22,1,.36,1), box-shadow .28s",
                    // v3.9: subtle highlight only — a soft halo in the
                    // badge's own color; siblings are never dimmed
                    boxShadow: active
                      ? `0 0 0 4px ${ring}40, 0 6px 18px ${ring}55`
                      : "0 2px 6px rgba(0,0,0,.16)",
                  }}
                >
                  <span
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "center",
                      width: "100%", height: "100%", borderRadius: "50%",
                      background: "#fff", border: "2.5px solid #fff",
                      overflow: "hidden",
                    }}
                  >
                    {showMono ? (
                      <span style={{ fontWeight: 900, fontSize: 19, color: ring }}>{b.mono}</span>
                    ) : (
                      <img
                        src={logoSrc(b.id)}
                        alt={`${b.label} logo`}
                        draggable={false}
                        onError={() => setFailed((f) => ({ ...f, [b.id]: true }))}
                        style={{
                          width: "72%", height: "72%", objectFit: "contain",
                          pointerEvents: "none", display: "block",
                        }}
                      />
                    )}
                  </span>
                </button>
              </div>
            );
          })}
        </div>

      </div>

      <div style={{ minHeight: 22, marginTop: 12, fontSize: 13.5, fontWeight: 600, color: "#3d3d3d" }}>
        {caption}
      </div>
    </div>
  );
}
